<script lang="ts" setup>
import { format } from 'date-fns'

import { copyToClipboard } from '@/utils/copyToClipboard'

import { useAppStore } from '@/store/app/app'
import { SnackbarType } from '@/store/app/app.types'
import { useCityStore } from '@/store/city/city'

import { Code } from '@/api/modules/sales/types'

import giftActiveIcon from '@/assets/images/gift-active.webp'
import giftInactiveIcon from '@/assets/images/gift-inactive.png'

const { currencySymbol } = useCityStore()
const { showSnackbar } = useAppStore()

const DEFAULT_STATUS = 'Подарок'

type Props = {
  promocode: Code
  shortDescription: string
  isActiveCode?: boolean
}

const props = defineProps<Props>()

const isGift = props.isActiveCode ? giftActiveIcon : giftInactiveIcon
const variantStatus = props.isActiveCode
  ? 'var(--ui-primary)'
  : 'var(--ui-unactive-gray)'

const formalizedTimeOfAction = computed(() => {
  if (!props.isActiveCode) {
    return (
      props.promocode.error || `Превышено количество использований промокода`
    )
  }

  if (!props.promocode?.date_from || !props.promocode?.date_to) {
    return ''
  }

  // Добавлено время, чтобы не возникало ошибки, когда дата получалась -1 день
  const dateFrom = format(
    new Date(`${props.promocode?.date_from}T00:00:00`),
    'dd.MM.yyyy',
  )
  const dateTo = format(
    new Date(`${props?.promocode.date_to}T00:00:00`),
    'dd.MM.yyyy',
  )

  return `Срок действия с ${dateFrom} по ${dateTo}`
})

const copyCode = (promocode: string) => {
  const successful = copyToClipboard(promocode)

  if (successful) {
    showSnackbar({
      message: 'Промокод скопирован',
      type: SnackbarType.SUCCESS,
    })
  }
}
</script>

<template>
  <div class="promocode">
    <span class="promocode__title-card">Промокод:</span>

    <div
      class="promocode__card"
      :class="{ 'promocode__card--active': isActiveCode }"
    >
      <div class="promocode__card-headline">
        <div class="promocode__card-promocode">
          <div
            class="promocode__card-image"
            :style="{ 'background-image': `url(${isGift})` }"
          />
          <span class="promocode__card-promo">{{ promocode.promocode }}</span>
        </div>

        <the-status :text="DEFAULT_STATUS" :bg-color="variantStatus" />
      </div>
      <div class="promocode__card-title">
        {{
          `${promocode.title} при заказе от ${promocode.min_price} ${currencySymbol}`
        }}
      </div>

      <p class="promocode__card-time">{{ formalizedTimeOfAction }}</p>

      <the-button
        v-if="promocode.promocode"
        block
        size="sm"
        font="sm"
        icon="copy"
        indent-left
        variant="tertiary"
        class="promocode__card-button"
        @click="copyCode(promocode.promocode)"
      >
        <span>Скопировать</span>
      </the-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.promocode {
  color: var(--ui-text-primary);

  &__title-card {
    margin-bottom: 20px;
    font-size: 16px;
    font-weight: 800;
    line-height: 19px;
  }
  &__card {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    width: 335px;
    padding: 24px 20px;
    border-radius: 16px;
    box-shadow: var(--ui-card-shadow);

    @include media('<=portrait-phone') {
      width: 100%;
    }

    &-headline {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      margin-bottom: 32px;
    }

    &-image {
      width: 24px;
      height: 24px;
      margin-right: 10px;
      background-size: contain;
    }

    &-promocode {
      display: flex;
      align-items: center;
      font-size: 18px;
      font-weight: 800;
      line-height: 19px;
      color: var(--ui-unactive-gray);
      letter-spacing: 0.05em;
      cursor: pointer;
    }

    &-title {
      font-size: 14px;
      font-weight: 600;
      line-height: 20px;
      color: var(--ui-unactive-gray);
    }

    &-time {
      margin-top: 10px;
      font-size: 12px;
      font-weight: 600;
      line-height: 14px;
      color: var(--ui-unactive-gray);
    }

    &-button {
      margin-top: 28px;
    }

    &--active {
      .promocode__card-promo {
        color: var(--ui-text-primary);
      }

      .promocode__card-title {
        color: var(--ui-text-primary);
      }
    }
  }
}
</style>
